import React from 'react';
import PropTypes from 'prop-types';
import { TriangleAlert } from 'lucide-react';
import { TextField, CircularProgress } from '@mui/material';
import Button from 'tailwind-components/Button/Button';
import AutocompleteInput from 'components/Autocomplete/AutocompleteInput';

function AgencyMultiSelectBar({
  id,
  options,
  value,
  onChange,
  loading = false,
  isDirty = false,
  onUpdate,
  updateDisabled = false,
  timesInputClosed = 0,
  selectorDataTour,
  updateDataTour
}) {
  return (
    <div className='flex flex-col gap-3'>
      <div className='flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-3'>
        <div data-tour={selectorDataTour} className='min-w-0 flex-1'>
          <AutocompleteInput
            multiple
            options={options}
            isOptionEqualToValue={(option, v) => option.value === v.value}
            id={id}
            value={value}
            getOptionLabel={option => option.name}
            onValueChanges={onChange}
            disabled={loading}
            selectAll
            timesInputClosed={timesInputClosed}
            renderInput={params => (
              <TextField
                {...params}
                label='Agencies'
                placeholder='Select agencies'
                error={value.length === 0 && !loading}
                helperText={
                  value.length === 0 && !loading
                    ? 'You need to select at least 1 agency'
                    : ''
                }
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? (
                        <CircularProgress color='inherit' size={18} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />
        </div>
        <div data-tour={updateDataTour}>
          <Button
            variant='primaryLight'
            size='xsmall'
            text='Update'
            onClick={onUpdate}
            isDisabled={updateDisabled}
            className='shrink-0'
          />
        </div>
      </div>
      {isDirty && !loading && value.length > 0 && (
        <p className='m-0 flex items-center gap-1.5 text-xs text-amber-600'>
          <TriangleAlert className='size-3.5 shrink-0' />
          The displayed information is outdated for your current agency
          selection — press Update to reload.
        </p>
      )}
    </div>
  );
}

AgencyMultiSelectBar.propTypes = {
  id: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  ).isRequired,
  value: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  isDirty: PropTypes.bool,
  onUpdate: PropTypes.func.isRequired,
  updateDisabled: PropTypes.bool,
  timesInputClosed: PropTypes.number,
  selectorDataTour: PropTypes.string,
  updateDataTour: PropTypes.string
};

export default AgencyMultiSelectBar;
